import React, { Component, Fragment } from 'react'
import { withRouter } from 'react-router-dom'
import { inject, observer } from 'mobx-react'
import { Layout, PageHeader, Row, Col, Card, Button, Icon, Popconfirm, Spin, Modal, message } from 'antd'
import { users, files, subscriptions, policyParsers, tranzila, cancellationRequests } from '@services/api'
import { alertPopup } from '@utils/popup'
import {
    isPrepaymentActive,
    isPrepaymentExpired,
    isPrepaymentExpiringWithinMonth,
    isPrepaymentExpiringToday
} from '@utils/prepaymentDateUtils'
import Details from './Details'
import InsuranceCases from './InsuranceCases'
import Files from './Files'
import Graph from './Graph'
import UploadFilePopup from '@components/Popup/UploadFilePopup'
import ChooseFilePopup from '@components/Popup/ChooseFilePopup'
import LoginCompanyPopup from '@components/Popup/LoginCompanyPopup'
import AddClientDataPopup from '@components/Popup/AddClientDataPopup'
import CreateCancellationRequest from '@components/Popup/CreateCancellationRequest'
import AddPaymentLate from '@pages/Clients/Client/PaymentLate/AddPaymentLate'
import PaymentLateArchive from './PaymentLate/PaymentLateArchive'
import ClientTopSection from './TopSection'
import SubscriptionHistory from './SubscriptionHistory'
import SpecialSubscriptions from './SpecialSubscriptions'
import UserMessages from '../UserMessages'
import PaymentsData from './PaymentsData'

import CancelSubModal from '@components/Popup/CancelSubscriptionModal'
import StoDateMismatchModal from '@components/Popup/StoDateMismatchModal'
import { popupInitialState } from '@components/Popup/CancelSubscriptionModal/Utils'
import { isStoContractErrorKey, STO_CONTRACT_ERROR_TEXT } from '@pages/Tranzila/stoErrors'
import { buildTranzilaProductDetails } from '@utils/purchasePopupUtils'
import { canSyncCallMarker, callMarkerSyncMessage, callMarkerSyncUserId } from './callMarkerSync'

// TODO: CHECK BTN CANCEL SUB AND REQ TO CANCEL SUB, IF USER HAS PARENT ID DONT SHOW THEM

@inject((store: any) => {
    const { popupStore, authStore, subscriptionsStore, purchaseStore } = store
    return {
        popupStore,
        authStore,
        subscriptionsStore,
        purchaseStore
    }
})
@observer
class Client extends Component<any, any> {
    // The admin's manual CallMarker sync: a synchronous guard set before the request (one request per click, no retry),
    // released when it settles. ponytail: one sync per page at a time, also across client navigation (the flag is not
    // reset with the client state). A response for a client the page no longer shows is dropped, never displayed.
    callMarkerSyncRunning = false
    state = {
        subscriptionFlowBusy: false,
        callMarkerSyncBusy: false,
        clientInfo: {},
        clientCases: [],
        graph: [],
        notifications: [],
        loading: true,
        loading_sto: false,
        phoneInput: '',
        refreshHistory: false,
        showPaymentsPopup: false,
        wasPrepayment: false,
        isPrepaymentUser: false,
        forceClearPrepayment: false,
        showStoDatesModal: false,
        /// new cancellation popup data
        ...popupInitialState
    }

    componentDidMount() {
        this.updateInfo()
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.computedMatch.params.id
        const currentId = this.props.computedMatch.params.id
        if (prevId !== currentId) {
            // Reset stale client/sto state so the previous client's info is not
            // displayed under the new URL while the new data loads.
            this.setState(
                {
                    clientInfo: {},
                    clientCases: [],
                    graph: [],
                    notifications: [],
                    loading: true,
                    loading_sto: false,
                    showStoDatesModal: false,
                    ...popupInitialState
                },
                () => this.updateInfo()
            )
        }
    }

    reloadHarbCases = async () => {
        const {
            clientInfo: { id: user_id, files }
        } = this.state
        const {
            popupStore: { togglePopup }
        } = this.props

        this.setState({ loading: true })

        const docFiles = files.filter(file => file.type === 'doc')

        if (docFiles.length > 1) {
            togglePopup('chooseFile', {
                docFiles
            })
            this.setState({ loading: false })
        } else {
            await users.updateHarbCases({ user_id })
            this.updateInfo()
        }
    }

    reloadCompanyCases = async () => {
        const {
            clientInfo: { id: user_id }
        } = this.state
        this.setState({ loading: true })
        await policyParsers.updateUserPolicies({ user_id })
        this.updateInfo()
    }

    tryGetStoUserData = (status, user_sub_id) => {
        const user_id = this.props.computedMatch.params.id
        this.setState({ loading_sto: true }, async () => {
            tranzila
                .getStoTransactionData({ user_id, status, user_sub_id })
                .then(({ status, data }) => {
                    this.setState(p => ({
                        clientInfo: {
                            ...p.clientInfo,
                            sto_id: data.sto_id,
                            terminal_name: data.terminal_name,
                            sto_status: data.sto_status,
                            card_last_digits: data.card_last_digits,
                            expire_date: data.expire_date,
                            bank_code: data.bank_code,
                            bank_name: data.bank_name,
                            branch_code: data.branch_code,
                            account_number: data.account_number
                        },
                        loading_sto: false
                    }))
                })
                .catch(err => {
                    const data = (err && err.response && err.response.data) || {}
                    this.setState(p => ({
                        clientInfo: {
                            ...p.clientInfo,
                            sto_id: null,
                            terminal_name: null,
                            sto_status: null,
                            err_key: data.err_key || 'NO_STO_DATA',
                            err_message: data.err_message || null,
                            mismatch: data.err_key === 'FILTER_MISMATCH' ? data : null
                        },
                        loading_sto: false
                    }))
                })
        })
    }

    tryGetOppositeStoStatus = async () => {
        const user_id = this.props.computedMatch.params.id
        try {
            this.setState({ loading_sto: true })
            const { status } = await tranzila.getStoTransactionData({ user_id, status: 'active' })
            return status === 200
        } catch (err) {
            return false
        }
    }

    updateInfo = () => {
        this.setState({ loading: true }, async () => {
            const user_id = this.props.computedMatch.params.id

            let newClientInfo
            try {
                newClientInfo = (await users.getClient({ user_id })).data
            } catch (e) {
                // this is the site that blanks clientInfo while dropping the spinner,
                // so TopSection mounts with no id and card-status fires without user_id
                console.error('[Client]: updateInfo failed, clientInfo blanked', {
                    url_id: user_id,
                    status: e && e.response ? e.response.status : null,
                    data: e && e.response ? e.response.data : null,
                    message: e ? e.message : null
                })
                // Primary request failed: stop spinners and keep a safe state shape
                // so the page does not stay stuck on "loading" forever.
                this.setState({
                    loading: false,
                    loading_sto: false,
                    clientInfo: {},
                    clientCases: [],
                    graph: [],
                    notifications: []
                })
                return
            }

            let data = null
            try {
                const res = await users.getInfo({ user_id })
                data = res.data
            } catch (e) {
                // getClient succeeded but getInfo did not. only a problem if the
                // body we keep has no id
                if (!newClientInfo || !newClientInfo.id) {
                    console.error('[Client]: updateInfo failed and newClientInfo has no id', {
                        url_id: user_id,
                        newClientInfo,
                        message: e ? e.message : null
                    })
                }
                this.setState({ loading: false, clientInfo: newClientInfo })
                return
            }
            // `data` can exist while insuranceCases is null; guard before .filter so
            // updateInfo never throws and leaves loading stuck.
            const graph = (data && data.insuranceCases) || []
            const notifications = data ? data.notifications : null
            const clientCases = data ? graph.filter(policy => policy.policy_number) : null

            // Check subscription history for prepayment
            let wasPrepayment = false
            try {
                const historyRes = await users.getSubscriptionHistory({ user_id })
                const { sub_history } = historyRes.data
                if (sub_history && sub_history.length > 0) {
                    const firstItem = sub_history[0]
                    wasPrepayment = newClientInfo.subscription_status !== 'ACTIVE' && !!firstItem.prepayment_until
                }
            } catch (e) {
                console.error('Failed to fetch subscription history', e)
            }

            // Preserve prepayment fields if they exist in current state but not in new response.
            // If forceClearPrepayment is set (e.g. after a successful cancel), skip preservation
            // so the server value does not override the intentional local clear.
            const forceClear = this.state.forceClearPrepayment
            const clientInfo = {
                ...newClientInfo,
                prepayment_until: forceClear
                    ? null
                    : newClientInfo.prepayment_until !== undefined && newClientInfo.prepayment_until !== null
                    ? newClientInfo.prepayment_until
                    : (this.state.clientInfo as any).prepayment_until !== undefined &&
                      (this.state.clientInfo as any).prepayment_until !== null
                    ? (this.state.clientInfo as any).prepayment_until
                    : null,
                prepayment_since: forceClear
                    ? null
                    : newClientInfo.prepayment_since !== undefined && newClientInfo.prepayment_since !== null
                    ? newClientInfo.prepayment_since
                    : (this.state.clientInfo as any).prepayment_since !== undefined &&
                      (this.state.clientInfo as any).prepayment_since !== null
                    ? (this.state.clientInfo as any).prepayment_since
                    : null,
                prepayment_months: forceClear
                    ? null
                    : newClientInfo.prepayment_months !== undefined && newClientInfo.prepayment_months !== null
                    ? newClientInfo.prepayment_months
                    : (this.state.clientInfo as any).prepayment_months !== undefined &&
                      (this.state.clientInfo as any).prepayment_months !== null
                    ? (this.state.clientInfo as any).prepayment_months
                    : null
            }
            if (forceClear) {
                this.setState({ forceClearPrepayment: false })
            }

            // subscription_status
            // Don't overwrite wasPrepayment/isPrepaymentUser while the cancel popup is open — the
            // refetch after status change would reset them mid-flow and flash the second step.
            const nextWasPrepayment = this.state.showCancelPopup ? this.state.wasPrepayment : wasPrepayment
            const nextIsPrepaymentUser = this.state.showCancelPopup
                ? this.state.isPrepaymentUser
                : !!(clientInfo as any).prepayment_until
            // the happy path finished but the body carries no id
            if (!clientInfo || !(clientInfo as any).id) {
                console.error('[Client]: updateInfo success path but clientInfo has no id', {
                    url_id: user_id,
                    clientInfo
                })
            }
            this.setState(
                {
                    loading: false,
                    clientInfo,
                    clientCases,
                    graph,
                    notifications,
                    wasPrepayment: nextWasPrepayment,
                    isPrepaymentUser: nextIsPrepaymentUser
                },
                () => {}
            )

            // Check for prepayment expiry warning
            if (clientInfo.prepayment_until) {
                // Show warning from now until the day OF prepayment_until (inclusive)
                if (isPrepaymentExpiringWithinMonth(clientInfo.prepayment_until)) {
                    const isToday = isPrepaymentExpiringToday(clientInfo.prepayment_until)
                    const formattedDate = new Date(clientInfo.prepayment_until).toLocaleDateString('he-IL')
                    const body = isToday
                        ? 'תוקף התשלום מראש של הלקוח מסתיים היום — זהו היום האחרון לחידוש. יש לחדש את התשלום כדי להמשיך את השירות ללא הפרעה.'
                        : `תוקף התשלום מראש של הלקוח יסתיים בתאריך ${formattedDate}.\nיש לחדש את התשלום כדי להמשיך את השירות.`
                    alertPopup({
                        type: 'alert',
                        title: 'התראה על התשלום מראש',
                        body
                    })
                }
            }

            // dont check for family members
            if (clientInfo.parent_id) return

            const checkStatus = ['ACTIVE', 'BLOCKED']

            if (clientInfo.subscription_id && checkStatus.includes(clientInfo.subscription_status)) {
                this.tryGetStoUserData('active', clientInfo.user_subscription_id)
            } else {
                const checkHasActive = await this.tryGetOppositeStoStatus()
                if (checkHasActive) {
                    this.setState(p => ({
                        clientInfo: {
                            ...p.clientInfo,
                            sto_id: null,
                            terminal_name: null,
                            sto_status: null,
                            err_key: 'OPPOSITE_STO_DATA'
                        },
                        loading_sto: false
                    }))
                } else {
                    this.setState(p => ({
                        loading_sto: false
                    }))
                }
            }
        })
    }

    togglePaymentsPopup = (visible: boolean) => this.setState({ showPaymentsPopup: visible })

    toggleStoDatesModal = visible => this.setState({ showStoDatesModal: visible })

    handleUpdateStoDate = async sto_created_at => {
        const { id: user_id, user_subscription_id, mismatch } = this.state.clientInfo
        try {
            const res = await tranzila.updateStoCreatedAt({
                user_id,
                user_subscription_id,
                sto_id: mismatch && mismatch.sto_id,
                sto_created_at // 'YYYY-MM-DD HH:mm:ss'
            })
            if (res.status === 200) {
                this.toggleStoDatesModal(false)
                this.setState({ refreshHistory: true })
                this.updateInfo() // рефреш страницы клиента
                return true
            }
            return false
        } catch (e) {
            console.log(e && e.response && e.response.data)
            return false
        }
    }

    getClientInfo = () => {
        const user_id = this.props.computedMatch.params.id
        users.getClient({ user_id }).then(async res => {
            const newClientInfo = res.data
            // Preserve prepayment fields if they exist in current state but not in new response.
            // If forceClearPrepayment is set (e.g. after a successful cancel), skip preservation.
            const forceClear = this.state.forceClearPrepayment
            const clientInfo = {
                ...newClientInfo,
                prepayment_until: forceClear
                    ? null
                    : newClientInfo.prepayment_until !== undefined && newClientInfo.prepayment_until !== null
                    ? newClientInfo.prepayment_until
                    : (this.state.clientInfo as any).prepayment_until !== undefined &&
                      (this.state.clientInfo as any).prepayment_until !== null
                    ? (this.state.clientInfo as any).prepayment_until
                    : null,
                prepayment_since: forceClear
                    ? null
                    : newClientInfo.prepayment_since !== undefined && newClientInfo.prepayment_since !== null
                    ? newClientInfo.prepayment_since
                    : (this.state.clientInfo as any).prepayment_since !== undefined &&
                      (this.state.clientInfo as any).prepayment_since !== null
                    ? (this.state.clientInfo as any).prepayment_since
                    : null,
                prepayment_months: forceClear
                    ? null
                    : newClientInfo.prepayment_months !== undefined && newClientInfo.prepayment_months !== null
                    ? newClientInfo.prepayment_months
                    : (this.state.clientInfo as any).prepayment_months !== undefined &&
                      (this.state.clientInfo as any).prepayment_months !== null
                    ? (this.state.clientInfo as any).prepayment_months
                    : null
            }
            if (forceClear) {
                this.setState({ forceClearPrepayment: false })
            }
            // refresh path. it overwrites clientInfo while TopSection is already mounted,
            // so an id-less body here triggers componentDidUpdate -> initializeCardStatus
            if (!clientInfo || !(clientInfo as any).id) {
                console.error('[Client]: getClientInfo refresh returned no id', {
                    url_id: user_id,
                    resData: res.data
                })
            }
            this.setState({ clientInfo, isPrepaymentUser: !!(clientInfo as any).prepayment_until })
            if (res.data.parent_id) return

            if (
                res.data.subscription_id &&
                (res.data.subscription_status === 'ACTIVE' || res.data.subscription_status === 'BLOCKED')
            ) {
                this.tryGetStoUserData('active', res.data.user_subscription_id)
            } else {
                const checkHasActive = await this.tryGetOppositeStoStatus()
                if (checkHasActive) {
                    this.setState(p => ({
                        clientInfo: {
                            ...p.clientInfo,
                            sto_id: null,
                            terminal_name: null,
                            sto_status: null,
                            err_key: 'OPPOSITE_STO_DATA'
                        },
                        loading_sto: false
                    }))
                } else {
                    this.setState(p => ({
                        loading_sto: false
                    }))
                    // console.log();
                    // this.tryGetStoUserData('inactive')
                }
            }
            // if (res.data.subscription_id) {
            //     const status = res.data.subscription_status === 'ACTIVE' ? 'active' : 'inactive'
            //     this.tryGetStoUserData(status)
            // }
        })
        .catch(e => {
            // this chain had no catch, so a failed refresh was an unhandled rejection.
            // it does not blank clientInfo, but it is worth seeing when ruling out A-D
            console.error('[Client]: getClientInfo request failed', {
                url_id: user_id,
                status: e && e.response ? e.response.status : null,
                message: e ? e.message : null
            })
        })
        // this.updateInfo()
    }

    handleRemoveUser = async () => {
        const id = this.props.computedMatch.params.id
        try {
            await users.deleteClient(id)
            this.props.history.push(`/clients`)
        } catch (err) {
            const msg = err && err.response && err.response.data && err.response.data.msg
            if (msg) {
                alert(msg)
            }
        }
    }

    sendCancelMail = () => {
        const { clientInfo } = this.state
        if (!clientInfo || !clientInfo.id) return

        Modal.confirm({
            title: 'שליחת מייל ביטול',
            content: (
                <div style={{ direction: 'rtl', textAlign: 'right' }}>לשלוח ללקוח את מכתב אישור ביטול המינוי?</div>
            ),
            okText: 'שלח',
            cancelText: 'ביטול',
            onOk: () =>
                cancellationRequests
                    .sendCancelMail({ userId: clientInfo.id })
                    .then(() => message.success('המייל נשלח'))
                    .catch(() => message.error('שליחת המייל נכשלה'))
        })
    }

    toggleCancelPopup = (visible, initiator, pay_until = null) => {
        const {
            clientInfo: { sto_id, err_key, subscription_status, sto_status, prepayment_until, collective_id }
        } = this.state
        const normalizedVisible = typeof visible === 'boolean' ? visible : true
        const isPrepayment = isPrepaymentActive(prepayment_until)
        // EARLY RETURN: closing popup should just close and reset state,
        // never fall through to cancellation logic below.
        if (!normalizedVisible && initiator === 'CLOSE_POPUP') {
            this.setState({ showCancelPopup: false })
            return
        }
        let eventInitiator = initiator || 'REGULAR_CANCELLATION'
        const listBlocked = ['REGULAR_CANCELLATION', 'SUBSCRIPTION_ACTIVE']

        if (eventInitiator.includes('SUBSCRIPTION_RECOVER')) {
            const splited = eventInitiator.split(',')
            eventInitiator = splited[0]
            this.setState({ recoverSubscriptionId: splited[1] })
        }

        // check is prepayment or collective
        if (isPrepayment || pay_until || collective_id) {
            this.setState({ tranzilaApproval: false, showCancelPopup: normalizedVisible, eventInitiator }, () => {
                this.handleCanceledSubscription()
            })
            return
        }

        if (subscription_status === 'BLOCKED' && sto_id && listBlocked.includes(eventInitiator)) {
            if (eventInitiator === 'REGULAR_CANCELLATION') {
                if (sto_status === 'active') {
                    this.setState({
                        tranzilaApproval: true,
                        eventInitiator,
                        cancellationProcess: popupInitialState.cancellationProcess,
                        showCancelPopup: normalizedVisible
                    })
                    return
                } else if (sto_status === 'inactive') {
                    // Auto-trigger: no Tranzila step needed for this combination (sto_status / err_key),
                    // so we skip showing the popup and run the local-only cancellation flow directly.
                    this.setState(
                        { tranzilaApproval: false, showCancelPopup: normalizedVisible, eventInitiator },
                        () => {
                            this.handleCanceledSubscription()
                        }
                    )
                    return
                }
            } else if (eventInitiator === 'SUBSCRIPTION_ACTIVE') {
                if (sto_status === 'inactive') {
                    this.setState({
                        tranzilaApproval: true,
                        eventInitiator,
                        cancellationProcess: popupInitialState.cancellationProcess,
                        showCancelPopup: normalizedVisible
                    })
                    return
                } else if (sto_status === 'active') {
                    // Auto-trigger: no Tranzila step needed for this combination (sto_status / err_key),
                    // so we skip showing the popup and run the local-only cancellation flow directly.
                    this.setState(
                        { tranzilaApproval: false, showCancelPopup: normalizedVisible, eventInitiator },
                        () => {
                            this.handleCanceledSubscription()
                        }
                    )
                    return
                }
            }
        }

        if (!sto_id && normalizedVisible) {
            if (
                (err_key === 'OPPOSITE_STO_DATA' && eventInitiator === 'REGULAR_CANCELLATION') ||
                eventInitiator === 'SUBSCRIPTION_RECOVER'
            ) {
                this.setState({
                    tranzilaApproval: true,
                    eventInitiator,
                    cancellationProcess: popupInitialState.cancellationProcess,
                    showCancelPopup: normalizedVisible
                })
            } else if (isStoContractErrorKey(err_key)) {
                // The standing order could not be read (unusable Tranzila response): this is NOT "no standing
                // order", so the local-only cancellation must not run. Manual check.
                message.error(STO_CONTRACT_ERROR_TEXT)
            } else {
                // Auto-trigger: no Tranzila step needed for this combination (sto_status / err_key),
                // so we skip showing the popup and run the local-only cancellation flow directly.
                this.setState({ tranzilaApproval: false, showCancelPopup: normalizedVisible, eventInitiator }, () => {
                    this.handleCanceledSubscription()
                })
            }
        } else {
            this.setState({
                tranzilaApproval: true,
                eventInitiator,
                cancellationProcess: popupInitialState.cancellationProcess,
                showCancelPopup: normalizedVisible
            })
        }
    }

    cancelStepFailed = msg => {
        this.setState(p => ({
            cancellationProcess: { ...p.cancellationProcess, failed: true, process: false, errorMsg: msg }
        }))
        return false
    }

    cancelStepSuccess = step => {
        this.setState(p => ({ cancellationProcess: { ...p.cancellationProcess, step } }))
        return true
    }

    preStepRun = step => {
        this.setState(p => ({
            cancellationProcess: {
                ...p.cancellationProcess,
                step,
                process: true,
                awaitingStepsCount: this.state.tranzilaApproval ? 2 : 1
            }
        }))
    }

    tranzilaApproveHandler = e => {
        this.setState({ tranzilaApproval: e.target.checked })
    }

    cancelMainSubscription = async () => {
        const { subscription_id, id: user_id } = this.state.clientInfo
        try {
            const response = await subscriptions.canceledSub({ subscription_id, user_id })

            if (response.status === 200) return this.cancelStepSuccess(2)

            return this.cancelStepFailed('')
        } catch (e) {
            console.log(e && e.response && e.response.data)
            return this.cancelStepFailed('')
        }
    }
    cancelTraSubscription = async () => {
        const { recoverSubscriptionId, eventInitiator } = this.state
        const { id: user_id, user_subscription_id, sto_id, terminal_name } = this.state.clientInfo
        const statuses = {
            REGULAR_CANCELLATION: {
                search_status: 'active',
                update_status: 'inactive'
            },
            SUBSCRIPTION_BLOCKED: {
                search_status: 'active',
                update_status: 'inactive'
            },
            SUBSCRIPTION_ACTIVE: {
                search_status: 'inactive',
                update_status: 'active'
            },
            SUBSCRIPTION_RECOVER: {
                search_status: 'inactive',
                update_status: 'active'
            }
        }
        const search_status = statuses[eventInitiator].search_status || null
        const update_status = statuses[eventInitiator].update_status || null

        if (!search_status || !update_status) {
            return this.cancelStepFailed('')
        }

        try {
            const response = await tranzila.canceledSubTranzila({
                user_id,
                user_subscription_id: user_subscription_id || recoverSubscriptionId,
                search_status,
                update_status,
                stoId: sto_id,
                terminal: terminal_name
            })

            if (response.status === 200) return true

            return this.cancelStepFailed('')
        } catch (e) {
            console.log(e)
            return this.cancelStepFailed('תהליך נכשל')
        }
    }

    activationSubHandler = async () => {
        const { id: userId } = this.state.clientInfo
        const { eventInitiator } = this.state
        const status = eventInitiator === 'SUBSCRIPTION_BLOCKED' ? 'BLOCKED' : 'ACTIVE'
        try {
            const response = await subscriptions.updateSubscriptionStatus({ userId, status })

            if (response.status === 200) return this.cancelStepSuccess(2)

            return this.cancelStepFailed('')
        } catch (e) {
            console.log(e)
            return this.cancelStepFailed('תהליך נכשל')
        }
    }

    subscriptionRecovery = async () => {
        const { recoverSubscriptionId, clientInfo } = this.state
        const prepayment_until = (clientInfo as any).prepayment_until
        // Check if prepayment_until is expired
        if (isPrepaymentExpired(prepayment_until)) {
            // Trigger redo prepayment flow before recovery
            const { subscriptionsStore, purchaseStore, popupStore } = this.props
            const subscription_id = (clientInfo as any).subscription_id

            if (subscription_id) {
                const currentSubscription = subscriptionsStore.subscriptions.find(
                    (sub: any) => sub.id === subscription_id
                )
                if (currentSubscription) {
                    const productDetails = buildTranzilaProductDetails(currentSubscription, null)
                    ///productDetails.iteration_type = 'ONCE'
                    purchaseStore.setSelectedTranzilaProduct(productDetails)
                    popupStore.togglePopup('TranzilaPopup')
                }
            }
            return
        }

        try {
            const response = await users.subscriptionRecovery({ id: recoverSubscriptionId })

            if (response.status === 200) return this.cancelStepSuccess(2)

            return this.cancelStepFailed('')
        } catch (e) {
            console.log(e)
            return this.cancelStepFailed('תהליך נכשל')
        }
    }

    // Synchronous guard for the cancel / block / activate / recover flow: set before the first await (the fresh-status
    // lookup), so a second click or trigger cannot start a second flow; a state flag alone updates too late. Every step
    // below is awaited, so it is released only when the whole flow has returned (refusal, failure or completion).
    subscriptionFlowRunning = false

    handleCanceledSubscription = async () => {
        if (this.subscriptionFlowRunning) return
        this.subscriptionFlowRunning = true
        this.setState({ subscriptionFlowBusy: true })
        try {
            await this.runSubscriptionFlow()
        } finally {
            this.subscriptionFlowRunning = false
            this.setState({ subscriptionFlowBusy: false })
        }
    }

    // The admin's manual CallMarker sync (callMarkerSyncRunning: its synchronous guard, declared with the state).
    syncCallMarker = async () => {
        if (this.callMarkerSyncRunning) return
        const userId = callMarkerSyncUserId(this.props.computedMatch.params.id)
        if (!userId) return
        this.callMarkerSyncRunning = true
        this.setState({ callMarkerSyncBusy: true })
        let status: number | null = null
        let body: any = null
        try {
            const res = await users.syncCallMarker(userId)
            status = res.status
            body = res.data
        } catch (error) {
            const res = error && error.response
            status = res ? res.status : null
            body = res ? res.data : null
        } finally {
            this.callMarkerSyncRunning = false
            this.setState({ callMarkerSyncBusy: false })
        }
        if (this.props.computedMatch.params.id !== userId) return
        const { type, text } = callMarkerSyncMessage(status, body)
        message[type](text, 8)
    }

    runSubscriptionFlow = async () => {
        const { tranzilaApproval, eventInitiator } = this.state
        const user_id = this.props.computedMatch.params.id

        let freshStatus = null
        try {
            const res = await users.getClient({ user_id })
            freshStatus = res.data && res.data.subscription_status
        } catch (e) {
            freshStatus = null
        }

        if (eventInitiator === 'SUBSCRIPTION_RECOVER') {
            if (freshStatus === 'ACTIVE' || freshStatus === 'BLOCKED') {
                alertPopup({
                    type: 'alert',
                    title: 'המנוי כבר פעיל',
                    body: 'לא ניתן לשחזר את המנוי מכיוון שהוא כבר פעיל.'
                })
                return
            }
        } else if (eventInitiator === 'SUBSCRIPTION_BLOCKED') {
            if (freshStatus === 'BLOCKED') {
                alertPopup({
                    type: 'alert',
                    title: 'הלקוח כבר חסום',
                    body: 'לא ניתן לחסום את המנוי מכיוון שהוא כבר חסום.'
                })
                return
            }
        } else if (eventInitiator === 'SUBSCRIPTION_ACTIVE') {
            if (freshStatus === 'ACTIVE') {
                alertPopup({
                    type: 'alert',
                    title: 'הלקוח כבר פעיל',
                    body: 'לא ניתן להפעיל את המנוי מכיוון שהוא כבר פעיל.'
                })
                return
            }
        } else {
            // REGULAR_CANCELLATION
            if (freshStatus !== 'ACTIVE' && freshStatus !== 'BLOCKED') {
                alertPopup({
                    type: 'alert',
                    title: 'המנוי כבר מבוטל',
                    body: 'לא ניתן לבטל את המנוי מכיוון שהוא אינו פעיל.'
                })
                return
            }
        }

        this.preStepRun(1)
        // 'SUBSCRIPTION_RECOVER'
        //'SUBSCRIPTION_BLOCKED' : 'SUBSCRIPTION_ACTIVE'

        const firstStep =
            eventInitiator === 'SUBSCRIPTION_BLOCKED' || eventInitiator === 'SUBSCRIPTION_ACTIVE'
                ? await this.activationSubHandler()
                : eventInitiator === 'SUBSCRIPTION_RECOVER'
                ? await this.subscriptionRecovery()
                : await this.cancelMainSubscription()

        if (!firstStep) return

        const isBlockOrUnblock = eventInitiator === 'SUBSCRIPTION_BLOCKED' || eventInitiator === 'SUBSCRIPTION_ACTIVE'
        const prepaymentClear = isBlockOrUnblock
            ? {}
            : { prepayment_until: null, prepayment_since: null, prepayment_months: null }
        const forceClearFlag = !isBlockOrUnblock ? { forceClearPrepayment: true } : {}

        if (!tranzilaApproval) {
            this.setState(
                prevState => ({
                    clientInfo: {
                        ...prevState.clientInfo,
                        ...prepaymentClear
                    },
                    ...forceClearFlag,
                    refreshHistory: true,
                    recoverSubscriptionId: null,
                    cancellationProcess: { ...prevState.cancellationProcess, success: true, process: false, step: 1 }
                }),
                () => this.updateInfo()
            )
            return
        }

        const secondStep = await this.cancelTraSubscription()

        if (!secondStep) {
            this.setState(
                prevState => ({
                    clientInfo: {
                        ...prevState.clientInfo,
                        ...prepaymentClear
                    },
                    ...forceClearFlag,
                    refreshHistory: true,
                    recoverSubscriptionId: null,
                    cancellationProcess: { ...prevState.cancellationProcess, success: true, process: false, step: 1 }
                }),
                () => this.updateInfo()
            )
            return
        }

        this.setState(
            prevState => ({
                clientInfo: {
                    ...prevState.clientInfo,
                    ...prepaymentClear
                },
                ...forceClearFlag,
                refreshHistory: true,
                recoverSubscriptionId: null,
                cancellationProcess: { ...prevState.cancellationProcess, success: true, process: false }
            }),
            () => this.updateInfo()
        )
    }

    resetRefreshHistory = () => this.setState({ refreshHistory: false })

    handleDeleteFile = async id => {
        await files.delete({ id })
        this.updateInfo()
    }

    generateInvoice = async id => {
        await files.invoice({ id })
        this.updateInfo()
    }

    render() {
        const {
            clientCases,
            graph,
            notifications,
            loading,
            phoneInput,
            clientInfo,
            showCancelPopup,
            cancellationProcess,
            tranzilaApproval,
            loading_sto,
            wasPrepayment,
            isPrepaymentUser
        } = this.state

        const {
            authStore: { roles },
            popupStore
        } = this.props

        const addPhone =
            clientInfo['related_as'] && !clientInfo['phone'] ? (
                <span style={{ marginRight: '22px', display: 'flex', alignItems: 'center' }}>
                    הוסף טלפון:
                    <input
                        value={phoneInput}
                        style={{ width: (phoneInput.length + 2) * 12 }}
                        onChange={e => this.setState({ phoneInput: e.target.value })}
                    />
                    0
                    <Popconfirm
                        title="האם וודאו פרטים בהר הביטוח וניתן להוסיף טלפון?"
                        onConfirm={() =>
                            users
                                .addRelativePhone({ phoneInput, id: clientInfo.id })
                                .then(() => {
                                    this.updateInfo()
                                })
                                .catch(() => alert('שגיאה בשמירת טלפון למשתשמ'))
                        }
                        okText="כן"
                        cancelText="לא"
                        disabled={
                            !(roles.includes('admin') || roles.includes('moderator') || roles.includes('developer')) ||
                            phoneInput.length != 9
                        }
                    >
                        <Button
                            type="primary"
                            style={{ marginRight: '20px' }}
                            disabled={
                                !(
                                    roles.includes('admin') ||
                                    roles.includes('moderator') ||
                                    roles.includes('developer')
                                ) || phoneInput.length != 9
                            }
                        >
                            הוסף טלפון
                        </Button>
                    </Popconfirm>
                </span>
            ) : (
                ''
            )

        return (
            <Layout style={{ padding: '0 1rem' }}>
                <PageHeader
                    onBack={() => window.history.back()}
                    title={
                        !loading ? (
                            <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12 }}>
                                {`${clientInfo['first_name']} ${clientInfo['last_name']} (${clientInfo['id']})`}
                                {clientInfo['collective_id'] && (
                                    <span style={{ color: '#1890ff', fontSize: 14, fontWeight: 'normal' }}>
                                        {'קולקטיב: '}
                                        <a
                                            style={{ cursor: 'pointer', color: 'black', textDecoration: 'underline' }}
                                            onClick={() => window.open(`/agents/${clientInfo['agent_id']}`, '_blank')}
                                        >
                                            {`${clientInfo['agent_name']} (${clientInfo['agent_id']})`}
                                        </a>
                                    </span>
                                )}
                                {addPhone}
                            </span>
                        ) : (
                            'טוען...'
                        )
                    }
                />
                {!loading ? (
                    <ClientTopSection
                        clientInfo={clientInfo}
                        getClientInfo={this.getClientInfo}
                        updateInfo={this.updateInfo}
                        loading_sto={loading_sto}
                        onConfirm={this.toggleCancelPopup}
                        onOpenStoDatesModal={() => this.toggleStoDatesModal(true)}
                    />
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <span>{'טוען...'}</span>
                    </div>
                )}

                <Row gutter={10}>
                    <Col>
                        <Card
                            headStyle={{ backgroundColor: '#dadce0' }}
                            bodyStyle={{ padding: '0.4rem 0.7rem' }}
                            style={{ marginBottom: '1rem' }}
                            title={
                                <>
                                    פרטי הלקוח
                                    {(roles.includes('admin') ||
                                        roles.includes('money') ||
                                        roles.includes('agents_moderator')) && (
                                        <Button
                                            style={{ float: 'left', margin: '-0.3rem 0.5rem' }}
                                            onClick={() => this.togglePaymentsPopup(true)}
                                        >
                                            <Icon type="credit-card" /> פירוט תשלומים
                                        </Button>
                                    )}
                                    {canSyncCallMarker(roles) && (
                                        <Popconfirm
                                            title="לסנכרן את הלקוח ל-CallMarker?"
                                            onConfirm={() => this.syncCallMarker()}
                                            okText="כן"
                                            cancelText="לא"
                                            disabled={this.state.callMarkerSyncBusy}
                                        >
                                            <Button
                                                icon="sync"
                                                style={{ float: 'left', margin: '-0.3rem 0.5rem' }}
                                                loading={this.state.callMarkerSyncBusy}
                                                disabled={this.state.callMarkerSyncBusy}
                                            >
                                                סנכרון CallMarker
                                            </Button>
                                        </Popconfirm>
                                    )}
                                </>
                            }
                        >
                            <Details info={clientInfo} />
                        </Card>
                    </Col>
                </Row>
                <Row gutter={10}>
                    <Col>
                        <Card
                            title={<Fragment>שיקוף תיק ביטוחי</Fragment>}
                            headStyle={{ backgroundColor: '#dadce0' }}
                            bodyStyle={{ padding: '0.8rem 0.8rem' }}
                            style={{ marginBottom: '1rem' }}
                        >
                            {!clientInfo['identity'] && clientInfo['passport'] ? (
                                <Graph list={graph} notifications={notifications} loading={loading} />
                            ) : null}
                        </Card>
                    </Col>
                </Row>

                {clientInfo.id && <SpecialSubscriptions clientInfo={clientInfo} />}

                <Row>
                    <Col>
                        <Card
                            title={
                                <Fragment>
                                    תיק ביטוחי
                                    {roles.includes('developer') && (
                                        <Button
                                            onClick={() =>
                                                this.props.history.push(
                                                    `/insurance-calculator?id=${this.props.computedMatch.params.id}`
                                                )
                                            }
                                            disabled={loading}
                                            style={{ float: 'left', margin: '-0.3rem 0.5rem -0.3rem 0rem' }}
                                        >
                                            <Icon type="calculator" /> יצירת דוח כפל חסר
                                        </Button>
                                    )}
                                    {clientInfo['status'] == 'COMPLETE' && !roles.includes('viewer') ? (
                                        <Button
                                            onClick={() => this.reloadHarbCases()}
                                            disabled={loading}
                                            style={{ float: 'left', margin: '-0.3rem 0.5rem' }}
                                        >
                                            <Icon type="reload" /> רענן מידע הר ביטוח
                                        </Button>
                                    ) : (
                                        ''
                                    )}
                                    {!roles.includes('viewer') && (
                                        <Button
                                            onClick={() => this.reloadCompanyCases()}
                                            disabled={loading}
                                            style={{ float: 'left', margin: '-0.3rem 0.5rem' }}
                                        >
                                            <Icon type="reload" /> רענן מידע חברות ביטוח
                                        </Button>
                                    )}
                                    <Button
                                        onClick={() =>
                                            popupStore.togglePopup('loginCompany', { ...clientInfo, clientCases })
                                        }
                                        disabled={loading}
                                        style={{ float: 'left', margin: '-0.3rem 0.5rem' }}
                                    >
                                        <Icon type="import" /> כניסה לחברות ביטוח
                                    </Button>
                                    {clientInfo['status'] !== 'COMPLETE' && (
                                        <Button
                                            style={{ float: 'left', margin: '-0.3rem 0.5rem' }}
                                            disabled={loading}
                                            onClick={() => popupStore.togglePopup('addClientData', { ...clientInfo })}
                                        >
                                            <Icon type="user" />
                                            עדכון מידע על לקוח
                                        </Button>
                                    )}
                                </Fragment>
                            }
                            headStyle={{ backgroundColor: '#dadce0' }}
                            bodyStyle={{ padding: '0.8rem 0.8rem' }}
                            style={{ marginBottom: '1rem' }}
                        >
                            <InsuranceCases list={clientCases} loading={loading} user_id={clientInfo['id']} />
                        </Card>
                    </Col>
                </Row>
                <Row gutter={10}>
                    <Col>
                        <Card
                            title={
                                <Fragment>
                                    קבצי לקוח
                                    {!roles.includes('viewer') && (
                                        <Button
                                            onClick={() =>
                                                popupStore.togglePopup('uploadFile', {
                                                    bucket: 'clients',
                                                    related: clientInfo['id'],
                                                    types: ['doc']
                                                })
                                            }
                                            disabled={loading}
                                            style={{ float: 'left', margin: '-0.3rem 2rem' }}
                                        >
                                            <Icon type="upload" /> העלאת קובץ
                                        </Button>
                                    )}
                                    {clientInfo['subscription_id'] &&
                                    (roles.includes('admin') ||
                                        roles.includes('moderator') ||
                                        roles.includes('developer') ||
                                        roles.includes('editor')) ? (
                                        <Button
                                            onClick={() => this.generateInvoice(clientInfo['id'])}
                                            style={{ float: 'left', margin: '-0.3rem 1rem' }}
                                        >
                                            צור אסמכתא
                                        </Button>
                                    ) : null}
                                </Fragment>
                            }
                            headStyle={{ backgroundColor: '#dadce0' }}
                            bodyStyle={{ padding: '0.8rem 0.8rem' }}
                            style={{ marginBottom: '1rem' }}
                        >
                            <Files
                                deleteFile={id => this.handleDeleteFile(id)}
                                list={clientInfo['files'] ? clientInfo['files'] : []}
                                loading={loading}
                            />
                        </Card>
                    </Col>
                </Row>

                {clientInfo.id && (
                    <SubscriptionHistory
                        clientInfo={clientInfo}
                        showRecoveryOption={roles.includes('admin') || roles.includes('money')}
                        updateInfo={this.updateInfo}
                        refreshHistory={this.state.refreshHistory}
                        resetRefreshHistory={this.resetRefreshHistory}
                        loadingSto={this.state.loading_sto}
                        onConfirm={this.toggleCancelPopup}
                        purchaseStatusVersion={this.props.purchaseStore.getPurchaseStatusVersion()}
                    />
                )}

                {clientInfo.payment_late_archive && (
                    <Row gutter={10}>
                        <Col>
                            <PaymentLateArchive clientInfo={clientInfo} />
                        </Col>
                    </Row>
                )}

                {clientInfo.id && (
                    <UserMessages
                        clientInfo={clientInfo}
                        purchaseStatusVersion={this.props.purchaseStore.getPurchaseStatusVersion()}
                    />
                )}

                <Row style={{ marginTop: '15px' }}>
                    {clientInfo['subscription_id'] && !clientInfo['parent_id'] && (
                        <Button
                            onClick={() => {
                                popupStore.togglePopup('CreateCancellationRequest', {
                                    title: 'בקשת ביטול מינוי',
                                    clientInfo
                                })
                            }}
                            style={{ float: 'left', marginRight: '15px' }}
                        >
                            בקשת ביטול מינוי
                        </Button>
                    )}
                    {clientInfo['subscription_id'] &&
                        !clientInfo['parent_id'] &&
                        (roles.includes('admin') ||
                            roles.includes('moderator') ||
                            roles.includes('developer') ||
                            roles.includes('money')) && (
                            <Popconfirm
                                title="האם לבצע ביטול מנוי?"
                                onConfirm={async () => {
                                    const user_id = this.props.computedMatch.params.id
                                    let freshStatus
                                    try {
                                        const res = await users.getClient({ user_id })
                                        freshStatus = res.data && res.data.subscription_status
                                    } catch (e) {
                                        freshStatus = null
                                    }
                                    if (freshStatus !== 'ACTIVE' && freshStatus !== 'BLOCKED') {
                                        alertPopup({
                                            type: 'alert',
                                            title: 'המנוי כבר מבוטל',
                                            body: 'לא ניתן לבטל את המנוי מכיוון שהוא אינו פעיל.'
                                        })
                                        return
                                    }
                                    this.toggleCancelPopup(true, 'REGULAR_CANCELLATION')
                                }}
                                okText="כן"
                                cancelText="לא"
                                disabled={loading_sto}
                            >
                                <Button
                                    type="danger"
                                    disabled={loading_sto}
                                    style={{ float: 'left', marginRight: '1rem' }}
                                >
                                    {loading_sto ? <Spin size="small" /> : 'ביטול מנוי'}
                                </Button>
                            </Popconfirm>
                        )}
                    {clientInfo['status'] !== 'DELETED' &&
                        (roles.includes('admin') || roles.includes('moderator') || roles.includes('developer')) && (
                            <Popconfirm
                                title="האם לבצע מחיקת משתמש?"
                                onConfirm={() => this.handleRemoveUser()}
                                okText="כן"
                                cancelText="לא"
                            >
                                {(roles.includes('admin') || roles.includes('developer')) && (
                                    <Button type="danger" style={{ float: 'left', marginRight: '1rem' }}>
                                        מחיקת משתמש
                                    </Button>
                                )}
                            </Popconfirm>
                        )}

                    {/*{(roles.includes('admin') || roles.includes('money') || roles.includes('agents_moderator')) &&*/}
                    {/*    clientInfo.can_send_cancellation_mail === true && (*/}
                    {/*        <Button onClick={this.sendCancelMail} style={{ float: 'left', marginRight: '1rem' }}>*/}
                    {/*            <Icon type="mail" style={{ marginLeft: '5px' }}/> שליחת מייל ביטול*/}
                    {/*        </Button>*/}
                    {/*    )}*/}

                    {!clientInfo.subscription_payment_late && (
                        <AddPaymentLate getClientInfo={this.getClientInfo} clientInfo={clientInfo} />
                    )}
                    <CreateCancellationRequest updateClientInfo={this.updateInfo} />
                </Row>
                <ChooseFilePopup refreshInsuranceCases={() => this.updateInfo()} />
                <UploadFilePopup fileUploaded={() => this.updateInfo()} />
                <LoginCompanyPopup />

                <AddClientDataPopup
                    title={`עדכון מידע על לקוח: ${clientInfo['first_name']} ${clientInfo['last_name']}`}
                    clientId={clientInfo['id']}
                    status={clientInfo['status']}
                />
                <PaymentsData
                    visible={this.state.showPaymentsPopup}
                    onClose={() => this.togglePaymentsPopup(false)}
                    clientId={clientInfo.id}
                    email={clientInfo.email}
                    firstName={clientInfo.first_name}
                    lastName={clientInfo.last_name}
                    identity={clientInfo.identity}
                    userIsCollective={clientInfo.collective_id}
                    roles={roles}
                />
                <CancelSubModal
                    visible={showCancelPopup}
                    onClose={() => this.toggleCancelPopup(false, 'CLOSE_POPUP')}
                    onAfterClose={() => this.setState({ ...popupInitialState })}
                    cancellation_process={cancellationProcess}
                    tranzilaApproval={tranzilaApproval}
                    handleCanceledSubscription={this.handleCanceledSubscription}
                    busy={this.state.subscriptionFlowBusy}
                    tranzilaApproveHandler={this.tranzilaApproveHandler}
                    eventInitiator={this.state.eventInitiator}
                    subsciptionStatus={this.state.clientInfo.subscription_status}
                    roles={roles}
                    isPrepayment={this.state.clientInfo.subscription_status === 'ACTIVE' && isPrepaymentUser}
                    wasPrepayment={
                        wasPrepayment || (this.state.clientInfo.subscription_status !== 'ACTIVE' && isPrepaymentUser)
                    }
                    isCollective={!!clientInfo.collective_id}
                />
                <StoDateMismatchModal
                    visible={this.state.showStoDatesModal}
                    onClose={() => this.toggleStoDatesModal(false)}
                    onSubmit={this.handleUpdateStoDate}
                    mismatch={clientInfo.mismatch}
                />
            </Layout>
        )
    }
}
export default withRouter(Client)
